import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { DateTime } from "luxon"
import { debounce } from "lodash"
import { Box } from "@mui/material"
import { enqueueSnackbar } from "notistack"
import FullCalendar from "@fullcalendar/react"
import luxonPlugin from "@fullcalendar/luxon3"
import timeGridPlugin from "@fullcalendar/timegrid"
import resourceTimelinePlugin from "@fullcalendar/resource-timeline"
import interactionPlugin, { EventResizeDoneArg } from "@fullcalendar/interaction"
import {
    DateSelectArg,
    DatesSetArg,
    EventClickArg,
    EventContentArg,
    EventDropArg,
    EventHoveringArg,
    EventSourceInput,
    EventApi,
    AllowFunc,
    DateSpanApi,
} from "@fullcalendar/core"
import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import {
    applyDynamicTimeBuffer,
    applyEventBufferStyle,
    getRoundedScrollTime,
    isAllDayAvailabilitySlot,
    isInPast,
    isOngoing,
    isSameResources,
    isSlotFullyInPast,
    scrollToResource,
    SLOT_DURATION,
    SLOT_SIZE,
    toFloatingIsoMinutesFromJsDate,
} from "./helpers"
import { selectSelectedLocale } from "../../../../../../modules/localization/localizationSelectors"
import { IsResourceAvailableFn } from "../resourceBookingCalendars/ResourceBookingCalendar"
import { EnvironmentVariable } from "../../../../../../modules/environment/envTypes"
import { earliestTime, isLocalEvent } from "../resourceBookingCalendars/helpers"
import { useAppSelector } from "../../../../../../modules/store/storeHooks"
import { EventTooltip } from "../resourceBookingCalendars/EventTooltip"
import { getEnvVariable } from "../../../../../../lib/getEnvVariable"
import { getFullCalendarConfig } from "./fullCalendarConfig"
import { DeliveryTimesField } from "../../form/formTypes"
import { useCalendarStyles } from "./useCalendarStyles"
import {
    TimelineCalendarEvent,
    TimelineCalendarResource,
    TimelineResourceGroupLabelContent,
    TimelineResourceLabelContent,
    ViewMode,
} from "./fullCalendarTypes"
import { GoToDatePicker } from "./GoToDatePicker"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import { useAutoEdgeTimelineNavigation } from "./useAutoEdgeTimelineNavigation"

type StandardTimelineCalendarProps = Readonly<{
    viewMode: ViewMode
    isLoadingEvents?: boolean
    resources: TimelineCalendarResource[]
    onDatesSet?: (arg: DatesSetArg) => void
    handleSelect?: (arg: DateSelectArg) => void
    handleEventDrop?: (arg: EventDropArg) => void
    resourceLabelContent: (arg: TimelineResourceLabelContent) => React.ReactNode
    resourceGroupLabelContent?: (arg: TimelineResourceGroupLabelContent) => React.ReactNode
    handleEventClick?: (arg: EventClickArg) => void
    handleEventResize?: (arg: EventResizeDoneArg) => void
    handleEventHover?: (arg: EventHoveringArg | null) => void
    eventSources: EventSourceInput[]
    localEvents?: TimelineCalendarEvent[]
    isBookedPeriod?: boolean
    isResourceAvailable?: IsResourceAvailableFn
    selectedDeliveries?: DeliveryTimesField
    isDraggingRef?: React.MutableRefObject<boolean>
    isUpdateMode?: boolean
}>

export function StandardTimelineCalendar({
    viewMode,
    isLoadingEvents = false,
    resources,
    onDatesSet,
    handleSelect,
    handleEventDrop,
    resourceLabelContent,
    resourceGroupLabelContent,
    handleEventClick,
    handleEventResize,
    handleEventHover,
    eventSources,
    localEvents,
    isBookedPeriod = false,
    isResourceAvailable,
    selectedDeliveries,
    isDraggingRef,
    isUpdateMode = false,
}: StandardTimelineCalendarProps) {
    const calendarRef = useRef<FullCalendar | null>(null)
    const containerRef = useRef<HTMLDivElement | null>(null)
    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()
    const isDayGrid = viewMode === "daysGrid"
    const { classes } = useCalendarStyles({
        isDayGrid,
        isEmptyResources: !resources.length,
        emptyResourcesText: l10n.getString("create-order-calendar-no-resources-message"),
    })
    const groupField = "location"
    const locale = useAppSelector(selectSelectedLocale)
    const calendarConfig = useMemo(
        () => getFullCalendarConfig(isDayGrid, l10n, calendarRef, dateTimeFactory, locale),
        [dateTimeFactory, isDayGrid, l10n, locale],
    )
    const debounceRef = useRef<ReturnType<typeof debounce> | null>(null)
    const initialLocalEventsRef = useRef<TimelineCalendarEvent[] | null>(null)
    const hasScrolledRef = useRef(false)
    const handleEventsDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const [shouldAutoScroll, setShouldAutoScroll] = useState(false)

    useAutoEdgeTimelineNavigation(dateTimeFactory, calendarRef, containerRef, isDayGrid)

    useEffect(() => {
        return () => {
            debounceRef.current?.cancel()
            if (handleEventsDebounceRef.current) {
                clearTimeout(handleEventsDebounceRef.current)
            }
        }
    }, [])

    useEffect(() => {
        if (!shouldAutoScroll || !handleEventsDebounceRef.current) return
        if (!hasScrolledRef.current) return
        const timer = setTimeout(() => {
            handleScrollToResource()
            setShouldAutoScroll(false)
        }, 300)
        return () => clearTimeout(timer)
    }, [shouldAutoScroll, localEvents])

    const handleEventMouseEnter = (arg: EventHoveringArg) => {
        if (isDraggingRef?.current) {
            handleEventHover?.(null)
            return
        }
        debounceRef.current?.cancel()
        debounceRef.current = debounce(() => {
            if (!isDraggingRef?.current) {
                handleEventHover?.(arg)
            }
        }, 450)
        debounceRef.current()
    }

    const handleEventMouseLeave = () => {
        debounceRef.current?.cancel()
        if (!isDraggingRef?.current) {
            handleEventHover?.(null)
        }
    }

    useEffect(() => {
        if (initialLocalEventsRef.current === null) {
            initialLocalEventsRef.current = localEvents ?? []
        }

        const wasPresentInitially = initialLocalEventsRef.current?.length > 0
        const hasEventsNow = localEvents && localEvents?.length > 0

        if (!wasPresentInitially || !hasEventsNow || hasScrolledRef.current || !calendarRef.current) {
            return
        }

        const timer = setTimeout(() => {
            const selectedEvent = localEvents.find((el) => isLocalEvent(el.id))
            const isoDateTime = selectedEvent?.start
            if (!isoDateTime) return

            const dateTime = dateTimeFactory.fromISO(isoDateTime).startOf("hour").minus({ hours: 1 })
            if (!dateTime.isValid) return

            if (!calendarRef.current) return
            const calendarApi = calendarRef.current.getApi()
            calendarApi.gotoDate(dateTime.toJSDate())
            calendarApi.scrollToTime(dateTime.toFormat("HH:mm:ss"))

            hasScrolledRef.current = true
        }, 100)

        return () => clearTimeout(timer)
    }, [localEvents])

    useEffect(() => {
        if (isBookedPeriod && localEvents?.length && calendarRef.current) {
            const selectedEvent = localEvents.find((el) => isLocalEvent(el.id))

            const dateTime = selectedEvent && dateTimeFactory.fromISO(selectedEvent.start)
            if (!dateTime) return
            const start = dateTime.startOf("hour")

            const calendarApi = calendarRef.current.getApi()
            applyDynamicTimeBuffer(calendarApi, start)

            calendarApi.gotoDate(dateTime.toJSDate())
        }
    }, [localEvents, isBookedPeriod])

    useEffect(() => {
        if (
            !localEvents?.length &&
            selectedDeliveries?.deliveryDate &&
            selectedDeliveries?.deliveryTimes?.length &&
            calendarRef.current
        ) {
            const date = dateTimeFactory.fromISO(selectedDeliveries.deliveryDate)
            if (date) {
                const calendarApi = calendarRef.current.getApi()
                calendarApi.gotoDate(date.toJSDate())
                calendarApi.scrollToTime(earliestTime(selectedDeliveries.deliveryTimes))
            }
        }
    }, [localEvents, selectedDeliveries])

    useEffect(() => {
        if (calendarRef.current && isDayGrid) {
            const calendarApi = calendarRef.current.getApi()
            const view = calendarApi.view

            handleDatesSet({
                view,
                start: view.currentStart,
                end: view.currentEnd,
                startStr: view.currentStart.toISOString(),
                endStr: view.currentEnd.toISOString(),
                timeZone: calendarApi.getOption("timeZone") ?? "local",
            })
        }
    }, [isDayGrid])

    const startInteraction = () => {
        if (isDraggingRef) isDraggingRef.current = true
        debounceRef.current?.cancel()
        handleEventHover?.(null)
    }

    const stopInteraction = () => {
        if (isDraggingRef) isDraggingRef.current = false
        debounceRef.current?.cancel()
        handleEventHover?.(null)
    }

    const handleAttemptInPast = () => {
        enqueueSnackbar({
            variant: "warning",
            message: l10n.getString("create-order-past-booking-attempt-message"),
        })
    }

    const getEventDisplayContent = (eventInfo: EventContentArg) => {
        const { event } = eventInfo
        const { start, end, id, extendedProps } = event
        const { outOfService, canEdit, subject } = extendedProps

        const startDt = start ? dateTimeFactory.fromJSDate(start) : null
        const endDt = end ? dateTimeFactory.fromJSDate(end) : null

        const formattedTime = [
            startDt ? dateTimeFactory.toHourMinute(startDt) : "",
            endDt ? dateTimeFactory.toHourMinute(endDt) : "",
        ]
            .filter(Boolean)
            .join(" - ")

        const isAllDaySlot = !!startDt && !!endDt && isAllDayAvailabilitySlot(dateTimeFactory, startDt, endDt)

        if (!isDayGrid) {
            return (
                <EventTooltip>
                    {isAllDaySlot ? l10n.getString("create-order-calendar-all-day-label") : formattedTime}
                </EventTooltip>
            )
        }
        if ((outOfService || canEdit) && subject) return subject
        return isLocalEvent(id) ? formattedTime : `#${id}`
    }

    const handleDatesSet = (arg: DatesSetArg) => {
        onDatesSet?.(arg)
        handleEventsDebounceRef.current = null

        // if (!calendarRef.current || !isDayGrid || isBookedPeriod) return

        // const calendarApi = calendarRef.current.getApi()
        // const viewStart = dateTimeFactory.fromJSDate(arg.start)
        // const now = dateTimeFactory.now()
        // const today = now.startOf("day")
        // const isToday = viewStart.hasSame(today, "day")
        // const nowHour = now.startOf("hour")
        // calendarApi.setOption("slotMinTime", isToday ? nowHour.toFormat("HH:mm:ss") : "00:00:00")
    }

    const eventAllow = useCallback<AllowFunc>(
        (dropInfo: DateSpanApi, draggedEvent: EventApi | null) => {
            if (!draggedEvent) return true

            const { start, end, resource } = dropInfo
            const draggedResIds = draggedEvent.getResources().map((r) => r.id)
            const dropResIds = resource?.id ? [resource.id] : draggedResIds

            if (isUpdateMode && isOngoing(draggedEvent, dateTimeFactory)) {
                const sameStart = draggedEvent.start && start && draggedEvent.start.getTime() === start.getTime()
                const sameResources = isSameResources(draggedResIds, dropResIds)
                const endFuture = !isInPast(end?.toISOString())
                return !!(sameStart && sameResources && endFuture)
            }

            if (isInPast(start?.toISOString())) return false

            if (isResourceAvailable) {
                return isResourceAvailable(
                    toFloatingIsoMinutesFromJsDate(dateTimeFactory, start),
                    toFloatingIsoMinutesFromJsDate(dateTimeFactory, end!),
                    dropResIds,
                    draggedEvent.id,
                )
            }

            return true
        },
        [isUpdateMode, isResourceAvailable],
    )

    const handleScrollToResource = () => {
        const selectedEvent = localEvents?.find((e) => isLocalEvent(e.id))
        const resourceId = selectedEvent?.resourceIds?.[0]
        if (!resourceId) return

        scrollToResource(resourceId)
    }
    const handleEventSet = () => {
        if (hasScrolledRef.current && !handleEventsDebounceRef.current) {
            if (handleEventsDebounceRef.current) {
                clearTimeout(handleEventsDebounceRef.current)
            }
            handleEventsDebounceRef.current = setTimeout(() => {
                setShouldAutoScroll(true)
            }, 200)
        }
    }

    const getEventClassNames = useCallback(
        (arg: { event: EventApi }) => {
            const classNames = ["custom-event"]

            // Week view: grey out slots that are fully in the past (incl. today's past slots)
            if (!isDayGrid && isSlotFullyInPast(dateTimeFactory, arg.event.end, dateTimeFactory.now())) {
                classNames.push("custom-event--past")
            }

            return classNames
        },
        [dateTimeFactory, isDayGrid],
    )

    return (
        <Box ref={containerRef} className={classes.container}>
            <FullCalendar
                ref={calendarRef}
                plugins={[resourceTimelinePlugin, timeGridPlugin, luxonPlugin, interactionPlugin]}
                schedulerLicenseKey={getEnvVariable(EnvironmentVariable.FULLCALENDAR_TOKEN)}
                {...calendarConfig}
                resourceGroupField={groupField}
                resources={resources}
                eventSources={eventSources}
                resourceLabelContent={resourceLabelContent}
                resourceLabelClassNames={classes.resourceLabel}
                resourceGroupLabelContent={resourceGroupLabelContent}
                resourceGroupLabelClassNames={classes.resourceGroupLabel}
                eventContent={getEventDisplayContent}
                eventDidMount={isDayGrid ? (info) => applyEventBufferStyle(info, resources) : undefined}
                eventClassNames={getEventClassNames}
                selectable={!isLoadingEvents && isDayGrid}
                eventsSet={() => isDayGrid && handleEventSet()}
                select={(arg) => {
                    if (isInPast(arg.startStr) || isInPast(arg.endStr)) {
                        handleAttemptInPast()
                        return
                    }

                    if (
                        isUpdateMode &&
                        localEvents?.some((evt) => {
                            const start = dateTimeFactory.fromISO(evt.start)
                            const end = dateTimeFactory.fromISO(evt.end)
                            const now = dateTimeFactory.now()
                            return start <= now && now < end
                        })
                    )
                        return
                    handleSelect?.(arg)
                }}
                editable={!isLoadingEvents && isDayGrid}
                eventDrop={(arg) => {
                    if (isDraggingRef) isDraggingRef.current = false
                    if (
                        !eventAllow(
                            {
                                start: arg.event.start!,
                                end: arg.event.end!,
                                startStr: toFloatingIsoMinutesFromJsDate(dateTimeFactory, arg.event.start!),
                                endStr: toFloatingIsoMinutesFromJsDate(dateTimeFactory, arg.event.end!),
                                allDay: arg.event.allDay ?? false,
                                resource: arg.newResource,
                            },
                            arg.event,
                        )
                    ) {
                        arg.revert()
                        handleAttemptInPast()
                        return
                    }
                    handleEventDrop?.(arg)
                }}
                eventAllow={eventAllow}
                eventDragStart={startInteraction}
                eventDragStop={stopInteraction}
                eventResizeStart={startInteraction}
                eventResizeStop={stopInteraction}
                titleFormat={"{LLLL {d}}, yyyy"}
                resourceOrder="type.name,title"
                scrollTime={getRoundedScrollTime(dateTimeFactory)}
                locale={locale}
                resourceAreaHeaderContent=""
                slotDuration={isDayGrid ? `00:${SLOT_DURATION}:00` : { days: 1 }}
                snapDuration={isDayGrid ? "00:05:00" : undefined}
                slotMinWidth={isDayGrid ? SLOT_SIZE : undefined}
                datesSet={handleDatesSet}
                eventClick={(arg) => {
                    const { event } = arg

                    if (isUpdateMode && isLocalEvent(event.id) && isOngoing(event, dateTimeFactory)) {
                        return
                    }

                    handleEventClick?.(arg)
                }}
                eventMouseEnter={handleEventMouseEnter}
                eventMouseLeave={isDayGrid && handleEventHover ? handleEventMouseLeave : undefined}
                eventResize={(arg) => {
                    if (isDraggingRef) isDraggingRef.current = false
                    if (
                        !eventAllow(
                            {
                                start: arg.event.start!,
                                end: arg.event.end!,
                                startStr: toFloatingIsoMinutesFromJsDate(dateTimeFactory, arg.event.start!),
                                endStr: toFloatingIsoMinutesFromJsDate(dateTimeFactory, arg.event.end!),
                                allDay: arg.event.allDay ?? false,
                                resource: arg.event.getResources()[0],
                            },
                            arg.event,
                        )
                    ) {
                        arg.revert()
                        handleAttemptInPast()
                        return
                    }
                    handleEventResize?.(arg)
                }}
                nowIndicator={isDayGrid}
            />
            <GoToDatePicker calendarRef={calendarRef} />
        </Box>
    )
}
